/**
 * RHFProductIntent
 * -----------------
 * RHF-native counterpart to ProductIntent — reads `bundle_as_product`,
 * `product_bundle_id` and `title` straight from the nearest FormProvider via
 * useFormContext + useWatch instead of taking them (and a dispatch bridge)
 * as props. On a successful edit-product save it resets the form to the new
 * merged values so RHF's dirty/baseline state stays in sync, mirroring what
 * the legacy `dispatchBundleConfig({ type: initState, init })` call did.
 */

import { Dispatch, SetStateAction } from "react";
import { Badge, Icon, Text, Thumbnail } from "@shopify/polaris";
import { EditIcon, ProductIcon } from "@shopify/polaris-icons";
import { useFormContext, useWatch } from "react-hook-form";
import styles from "./Intents.module.css";
import { endPoints, postData } from "../../utilities/networkCalls";

interface RHFProductIntentProps {
    shakeSaveBar: () => void;
    setSavingBundle: Dispatch<SetStateAction<boolean>>;
}

const fetchProductById = async (productId: string) => {
    const query = `
        query GetProductById($id: ID!) {
            product(id: $id) {
                id
                title
                descriptionHtml
                handle
                status
                images(first: 50) {
                    edges {
                        node {
                            id
                            url
                            altText
                        }
                    }
                }
            }
        }
    `;
    try {
        const res = await postData({
            url: endPoints.executeGraphql,
            payload: {
                query,
                variables: {
                    id: productId,
                },
            },
        });

        if (res?.status === 200 || res?.status === 201) {
            const product = res?.data?.data?.data?.product;
            if (!product) return null;

            return {
                id: product.id,
                title: product.title,
                handle: product.handle,
                status: product.status,
                descriptionHtml: product.descriptionHtml,
                images:
                    product.images?.edges?.map((edge: any) => ({
                        id: edge.node.id,
                        originalSource: edge.node.url,
                        alt: edge.node.altText ?? "img",
                        mediaContentType: "IMAGE",
                    })) ?? [],
            };
        }
        return null;
    } catch (error) {
        console.error("Failed to fetch product by ID", error);
        return null;
    }
};

const getBadgeTone = (status: string) => {
    switch (status) {
        case "DRAFT":
            return "attention";
        case "UNLISTED":
            return "info";
        case "ACTIVE":
            return "success";
        default:
            return undefined;
    }
};

const RHFProductIntent = ({ shakeSaveBar, setSavingBundle }: RHFProductIntentProps) => {
    const {
        control,
        getValues,
        reset,
        formState: { isDirty },
    } = useFormContext();

    const bundleAsProduct = useWatch({ control, name: "bundle_as_product" }) ?? {};
    const productBundleId = useWatch({ control, name: "product_bundle_id" }) ?? "";
    const title = useWatch({ control, name: "title" });

    const handleUpdateBundle = async (updatedConfig: any) => {
        try {
            shopify.saveBar.show("bundle-save-bar");
            setSavingBundle(true);

            if (!updatedConfig.form_id) return;
            const res = await postData({
                url: endPoints.updateBundle,
                payload: updatedConfig,
            });

            if (res.status === 200 || res.status === 201) {
                reset(updatedConfig);
                shopify.toast.show("Bundle Product Updated", { duration: 1500 });
            } else {
                shopify.toast.show("Failed to save data. Please try again.", {
                    isError: true,
                    duration: 2000,
                });
            }
        } catch (error) {
            shopify.toast.show("Bundle Product Updation Failed", { isError: true, duration: 1500 });
        } finally {
            setSavingBundle(false);
            shopify.saveBar.hide("bundle-save-bar");
        }
    };

    const handleEditProduct = async () => {
        try {
            if (isDirty) {
                shakeSaveBar();
                return;
            }
            // @ts-expect-error it exists
            const activity = await shopify.intents.invoke("edit:shopify/Product", {
                value: productBundleId,
            });

            const response = await activity.complete;

            if (response.code !== "ok") return;

            const productInfo = await fetchProductById(response.data.id);
            if (!productInfo) return;

            const currentValues = getValues();
            const updatedConfig = {
                ...currentValues,
                bundle_as_product: {
                    ...bundleAsProduct,
                    images: productInfo?.images,
                    product_title: productInfo?.title,
                    product_description: productInfo?.descriptionHtml,
                    product_status: productInfo?.status,
                },
                title: productInfo.title,
            };

            await handleUpdateBundle(updatedConfig);
        } catch (error) {
            console._log("Some error occured while updating bundle product intent.");
        }
    };

    return (
        <div className={styles.intentWrapper}>
            <div className={styles.intentHeader}>
                <div className={styles.intentHeaderLeft}>
                    <Text as="strong">Bundle Product</Text>
                    {bundleAsProduct.product_status && (
                        <Badge size="small" tone={getBadgeTone(bundleAsProduct.product_status)}>
                            {bundleAsProduct.product_status
                                .toLowerCase()
                                .replace(/^\w/, (c: string) => c.toUpperCase())}
                        </Badge>
                    )}
                </div>
            </div>

            <div className={styles.intentBody}>
                <div className={styles.intentBodyLeft}>
                    <Thumbnail
                        source={bundleAsProduct?.images?.[0]?.originalSource ?? ProductIcon}
                        alt={""}
                        size="medium"
                    />
                    <Text as="h5">{title}</Text>
                </div>
                <div className={styles.intentEditButton} onClick={handleEditProduct}>
                    <Icon source={EditIcon} tone={"subdued"} accessibilityLabel="Edit" />
                </div>
            </div>
        </div>
    );
};

export default RHFProductIntent;
